import { inject, InjectionToken, LOCALE_ID } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { from, of } from 'rxjs';
import {
  filter,
  map,
  mergeMap,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

import {
  TranslationLoadType,
  TranslationStatusService,
} from './services/translation-status.service';

export const FeatureTranslationKey = new InjectionToken<string>(
  'feature.translation.key',
);

export class FeatureTranslationModule {
  protected translationStatusService = inject(TranslationStatusService);
  protected readonly locale = inject(LOCALE_ID) as unknown as Promise<string>;
  protected translateService = inject(TranslateService);

  get translationExtends() {
    return !!this.translateService['extend'];
  }

  constructor() {
    from(this.locale)
      .pipe(
        switchMap((locale) =>
          this.translationStatusService.loadingCoreFeatures$.pipe(
            filter((loading) => !this.translationExtends || !loading),
            take(1),
            map(() => locale),
          ),
        ),
        tap(() =>
          this.translationStatusService.onLoadStart({
            type: TranslationLoadType.FEATURE,
            extend: this.translationExtends,
          }),
        ),
        mergeMap((locale) => {
          if (this.translateService.currentLang === undefined) {
            return of(locale);
          }
          return this.translateService.onLangChange.pipe(
            startWith({ lang: this.translateService.currentLang }),
            filter(({ lang }) => !!lang),
            map(({ lang }) => lang),
            tap(() => (this.translateService.currentLang = '')),
            take(1),
          );
        }),
        mergeMap((lang) => this.translateService.use(lang).pipe(take(1))),
        tap(() =>
          this.translationStatusService.onLoadEnd({
            type: TranslationLoadType.FEATURE,
            extend: this.translationExtends,
          }),
        ),
      )
      .subscribe();
  }
}
